import { NUM, TRY } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, AlertTriangle, PackagePlus, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { UNITS, itemCost, lineTotal } from "@/components/orders/orderFormUtils";

const SRC = {
  sistem: { label: "sistem", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  elle: { label: "elle", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  yok: { label: "iskonto tanımsız", cls: "bg-amber-50 text-amber-800 border-amber-300" },
};

const numIn = (v) => (v === "" ? "" : v);

// Form kalemleri: miktar/birim/not + liste fiyatı & maliyet iskontosu (düzenlenebilir) → net maliyet × miktar
export default function OrderFormItemsTable({ items, onChange, onRemove, onMove }) {
  const set = (key, patch) => onChange(items.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const setDisc = (it, v) => set(it.key, { cost_discount: numIn(v), discount_source: v === "" || Number(v) === 0 ? (it.custom ? "yok" : it.discount_source === "sistem" ? "yok" : it.discount_source) : "elle" });

  if (!items.length) {
    return (
      <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center text-muted-foreground border-2 border-dashed rounded-xl m-3" data-testid="of-items-empty">
        <PackagePlus className="h-9 w-9 opacity-30 mb-2" />
        <p className="font-semibold text-sm">Form boş</p>
        <p className="text-xs mt-1 max-w-xs">Soldan marka seçip ürünleri işaretleyin, açık talepleri aktarın veya stokta olmayan ürün ekleyin.</p>
      </div>
    );
  }
  return (
    <div className="overflow-auto h-full" data-testid="of-items">
      <table className="w-full min-w-[980px] text-[12px]">
        <thead className="sticky top-0 z-10">
          <tr className="bg-[#0B192C] text-white text-[10px] uppercase tracking-wider">
            <th className="px-2 py-2 text-left w-8">#</th>
            <th className="px-2 py-2 text-left">Ürün / Malzeme</th>
            <th className="px-2 py-2 text-right w-20">Miktar</th>
            <th className="px-2 py-2 text-left w-24">Birim</th>
            <th className="px-2 py-2 text-right w-28">Liste Fiyatı</th>
            <th className="px-2 py-2 text-right w-24">Mal. İsk. %</th>
            <th className="px-2 py-2 text-right w-28">Net Maliyet</th>
            <th className="px-2 py-2 text-right w-28">Tutar</th>
            <th className="px-2 py-2 text-left w-44">Açıklama / Not</th>
            <th className="px-1 py-2 w-16" />
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const src = SRC[it.discount_source] || SRC.yok;
            const warn = it.discount_source === "yok";
            return (
              <tr key={it.key} className={cn("border-b align-top hover:bg-muted/40", i % 2 ? "bg-slate-50/60" : "bg-white")} data-testid={`of-item-${i}`}>
                <td className="px-2 py-1.5 text-muted-foreground font-mono text-[11px]">{i + 1}</td>
                <td className="px-2 py-1.5 min-w-[300px]">
                  {it.custom ? (
                    <div className="space-y-1">
                      <Input value={it.name} onChange={(e) => set(it.key, { name: e.target.value })} placeholder="Ürün adı (stokta yok)" className="h-8 text-xs font-semibold w-full" data-testid={`of-item-name-${i}`} />
                      <div className="flex items-center gap-1.5">
                        <Input value={it.brand} onChange={(e) => set(it.key, { brand: e.target.value.toLocaleUpperCase("tr") })} placeholder="Marka" className="h-7 w-28 text-[11px]" data-testid={`of-item-brand-${i}`} />
                        <Input value={it.code} onChange={(e) => set(it.key, { code: e.target.value })} placeholder="Ürün kodu" className="h-7 w-32 text-[11px] font-mono" data-testid={`of-item-code-${i}`} />
                        <span className="text-[10px] text-primary inline-flex items-center gap-1 whitespace-nowrap"><PackagePlus className="h-3 w-3" />Stokta yok · fiyat/iskonto elle</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold leading-snug" data-testid={`of-item-name-${i}`}>{it.name}</div>
                      <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground mt-0.5 flex-wrap">
                        {it.brand && <span className="rounded-full bg-secondary/10 text-secondary px-1.5 font-semibold">{it.brand}</span>}
                        <span className="font-mono">{it.code || "-"}</span>
                        {it.stock != null && <span>Stok: <b className="tabular-nums text-foreground">{NUM(it.stock)}</b></span>}
                      </div>
                    </>
                  )}
                </td>
                <td className="px-2 py-1.5"><Input type="number" min="0" step="any" value={numIn(it.qty)} onChange={(e) => set(it.key, { qty: e.target.value })} className="h-8 text-right tabular-nums text-xs" data-testid={`of-item-qty-${i}`} /></td>
                <td className="px-2 py-1.5">
                  <Select value={it.unit || "ADET"} onValueChange={(v) => set(it.key, { unit: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`of-item-unit-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{[...new Set([...(UNITS), it.unit].filter(Boolean))].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1.5"><Input type="number" min="0" step="any" value={numIn(it.list_price)} onChange={(e) => set(it.key, { list_price: e.target.value })} className="h-8 text-right tabular-nums text-xs" data-testid={`of-item-list-${i}`} /></td>
                <td className="px-2 py-1.5">
                  <Input type="number" min="0" max="100" step="any" value={numIn(it.cost_discount)} onChange={(e) => setDisc(it, e.target.value)} className={cn("h-8 text-right tabular-nums text-xs", warn && "border-amber-400 bg-amber-50/60")} data-testid={`of-item-disc-${i}`} />
                  <span className={cn("mt-0.5 inline-flex items-center gap-0.5 rounded border px-1 text-[9px] font-bold leading-4", src.cls)} data-testid={`of-item-src-${i}`}>{warn && <AlertTriangle className="h-2.5 w-2.5" />}{src.label}</span>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold pt-3" data-testid={`of-item-cost-${i}`}>{TRY(itemCost(it))}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-black pt-3 text-[#0B192C]" data-testid={`of-item-total-${i}`}>{TRY(lineTotal(it))}</td>
                <td className="px-2 py-1.5"><Input value={it.note || ""} onChange={(e) => set(it.key, { note: e.target.value })} placeholder="Renk, ölçü, teslim notu…" className="h-8 text-xs" data-testid={`of-item-note-${i}`} /></td>
                <td className="px-1 py-1.5">
                  <div className="flex items-center justify-end gap-0.5">
                    <button type="button" onClick={() => onMove(i, -1)} disabled={i === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Yukarı" data-testid={`of-item-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => onMove(i, 1)} disabled={i === items.length - 1} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Aşağı" data-testid={`of-item-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => onRemove(it.key)} className="p-1 text-muted-foreground hover:text-destructive" title="Kaldır" data-testid={`of-item-rm-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
